// Pure validation for `/desktop-auth`'s query string (FR-A4). Split out from the page
// component so it is testable without a DOM/router (`desktopAuthParams.test.ts`).

export interface DesktopAuthParams {
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  redirect: string;
}

export type DesktopAuthParamsResult =
  | { ok: true; params: DesktopAuthParams }
  | { ok: false; error: string };

/** The one redirect URI the server accepts (`server/src/auth/handlers.rs::desktop_authorize`,
 * `grackle_core::desktop_auth::REDIRECT_URI`). Duplicated here rather than imported: this is a
 * TypeScript package with no dependency on the Rust crates. */
export const DESKTOP_REDIRECT_URI = "grackle://auth/callback";

/**
 * Reads `state`, `code_challenge`, `code_challenge_method`, `redirect` off `location.search`
 * (the exact query shape `grackle_core::desktop_auth::build_authorize_url` produces) and
 * checks presence plus the two values this page can actually act on. The server independently
 * re-validates `code_challenge_method`/`redirect_uri` on `POST /auth/desktop/authorize` — this
 * is a user-facing error message, not the security boundary.
 */
export function parseDesktopAuthParams(search: string): DesktopAuthParamsResult {
  const params = new URLSearchParams(search);
  const state = params.get("state");
  const codeChallenge = params.get("code_challenge");
  const codeChallengeMethod = params.get("code_challenge_method");
  const redirect = params.get("redirect");

  if (!state || !codeChallenge || !codeChallengeMethod || !redirect) {
    return {
      ok: false,
      error: "This sign-in link is missing required information. Try opening Grackle desktop again.",
    };
  }
  if (codeChallengeMethod !== "S256") {
    return { ok: false, error: "This sign-in link uses an unsupported method." };
  }
  if (redirect !== DESKTOP_REDIRECT_URI) {
    return { ok: false, error: "This sign-in link points somewhere unexpected." };
  }

  return { ok: true, params: { state, codeChallenge, codeChallengeMethod, redirect } };
}
